/**
 * The settings sections, in the order every list of them draws them: the
 * one registry a new section is added to. `id` is the `?section=` value,
 * and `group` is whose it is — Account is the signed-in person's alone,
 * Company is everything every Operator of the Company shares. The
 * sidebar, the phone's drill-down and the search all read this; what each
 * section draws is the settings page's (routes/settings), held to these
 * ids by its type.
 */
export const SETTINGS_SECTIONS = [
  { id: "profile", label: "Profile", group: "account" },
  { id: "general", label: "General", group: "company" },
  { id: "templates", label: "Documents & emails", group: "company" },
  { id: "payments", label: "Payments", group: "company" },
  // Suppliers is not a section: it is one small roster, and it lives
  // with the others behind Operations.
  { id: "operations", label: "Operations", group: "company" },
  // Data is not a section: the export is an outside service the
  // workspace connects to, so it is a group inside Integrations.
  { id: "integrations", label: "Integrations", group: "company" },
] as const;

export type SettingsSectionId = (typeof SETTINGS_SECTIONS)[number]["id"];

/** The groups the sections sit under, each said once as a small heading. */
export const SETTINGS_GROUPS = [
  { id: "account", label: "Account" },
  { id: "company", label: "Company" },
] as const;

/**
 * The Company's other settings, in `hireos-intake` (ADR-0016): the phone
 * number, the Receptionist, the inquiry email and the public form's
 * estimate. One link at the end of this app's own list, because an
 * Operator goes looking for a setting and not for a deployment — and one
 * link rather than a mirror of intake's list, which would go stale the
 * first time intake added a section. Intake's Settings is another app
 * behind its own sign-in, so the link leaves.
 */
export const INTAKE_SETTINGS_URL = "https://admin.hireos.co.nz/settings";

/** A settings section's place. */
export function settingsPath(section: SettingsSectionId): string {
  return `/settings?${new URLSearchParams({ section })}`;
}

/** The sections whose names hold every word typed, for the search. */
export function findSections(q: string) {
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  return SETTINGS_SECTIONS.filter((s) =>
    words.every((w) => s.label.toLowerCase().includes(w)),
  );
}
